import { Alert, AlertDescription, AlertTitle, Button, showErrorToast } from '@bpinternal/ui-kit-next'
import { AlertCircle, Trash2, UploadCloud, XCircle } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDropzone, type Accept, type FileRejection } from 'react-dropzone'
import { FileIcon } from '~/features/files/components/FileIcon'
import { FileStatus, getUseBotOnboardingStore } from '../../../stores/useBotOnboardingStore'
import { useDeleteFile, useUploadFile } from '~/hooks'
import { cn } from '~/utils'
import { Spinner } from '~/elements'

type FileUploaderProps = {
  botId: string
  workspaceId: string
  accept?: Accept
  maxSize?: number // in bytes
  maxFiles?: number
  disabled?: boolean
  className?: string
}

function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

function formatErrorMessage(error: { code: string; message: string }): string {
  switch (error.code) {
    case 'file-invalid-type':
      return 'File type not supported. Please upload .pdf, .txt, .doc, .docx, .html or .md files.'
    case 'file-too-large':
      return 'File is too large. Please choose a smaller file.'
    case 'file-too-small':
      return 'File is too small. Please choose a larger file.'
    case 'too-many-files':
      return 'Too many files. Please select fewer files.'
    default:
      return error.message
  }
}

export const FilesTabContent = ({ botId, workspaceId, accept, maxSize, disabled }: FileUploaderProps) => {
  const newBotStore = getUseBotOnboardingStore({ botId, workspaceId })
  const {
    knowledgeBase: { uploadableFiles },
    addUploadableFile,
    removeUploadableFile,
    updateUploadableFile,
  } = newBotStore()

  const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([])

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const previousFileCount = useRef(uploadableFiles.length)

  const totalFileSize = uploadableFiles
    .filter((f) => f.status !== 'error')
    .reduce((total, file) => total + file.fileSize, 0)

  const isSizeLimitReached = maxSize ? totalFileSize >= maxSize : false

  // Auto-scroll to new files when they're added
  useEffect(() => {
    if (uploadableFiles.length > previousFileCount.current && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
    previousFileCount.current = uploadableFiles.length
  }, [uploadableFiles.length])

  const { mutateAsync: uploadFile, isPending } = useUploadFile({ botId, workspaceId })
  const { mutateAsync: deleteFile } = useDeleteFile({ botId, workspaceId })

  const uploadFileToServer = useCallback(
    async (file: File, fileId: string) => {
      updateUploadableFile(fileId, { status: 'uploading' })

      try {
        const result = await uploadFile({
          key: `kb-default/${file.name}`,
          accessPolicies: [],
          content: file,
          contentType: file.type,
          index: true,
          tags: {
            source: 'knowledge-base',
            kbId: 'kb-default',
            title: file.name,
            initialBotFile: 'true',
          },
        })

        updateUploadableFile(fileId, {
          progress: 100,
          status: 'success',
          serverFileId: result?.file?.id,
        })
      } catch (error) {
        showErrorToast(`Failed to upload ${file.name}`)

        updateUploadableFile(fileId, {
          progress: 0,
          status: 'error',
          error: error instanceof Error ? error.message : 'Upload failed',
        })
      }
    },
    [botId, uploadFile, updateUploadableFile]
  )

  const onDrop = useCallback(
    (accepted: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        setRejectedFiles((prev) => [...prev, ...fileRejections])
      }

      // Filter files that would exceed the size limit
      const validFiles: File[] = []
      let currentTotalSize = totalFileSize

      for (const file of accepted) {
        if (maxSize && currentTotalSize + file.size > maxSize) {
          setRejectedFiles((prev) => [
            ...prev,
            {
              file,
              errors: [
                {
                  code: 'file-over-size-limit',
                  message: `File would exceed the ${formatBytes(maxSize)} total size limit`,
                },
              ],
            },
          ])
          continue
        }
        validFiles.push(file)
        currentTotalSize += file.size
      }

      validFiles.forEach((file) => {
        const fileId = crypto.randomUUID()
        uploadFileToServer(file, fileId)
        addUploadableFile({
          id: fileId,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          progress: 0,
          status: 'pending',
        })
      })
    },
    [maxSize, totalFileSize, addUploadableFile, uploadFileToServer]
  )

  const removeRejectedFile = (fileName: string) => {
    setRejectedFiles((prev) => prev.filter((rf) => rf.file.name !== fileName))
  }

  const handleDeleteFile = async (fileId: string, status: FileStatus) => {
    updateUploadableFile(fileId, { status: 'deleting' })

    if (status === 'error') {
      removeUploadableFile(fileId)
    } else if (status === 'success') {
      const fileToDelete = uploadableFiles.find((f) => f.id === fileId)
      if (fileToDelete?.serverFileId) {
        await deleteFile({ id: fileToDelete.serverFileId })
      }
      removeUploadableFile(fileId)
    }
  }

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    accept,
    maxSize,
    disabled: disabled || isSizeLimitReached || isPending,
  })

  return (
    <>
      <div
        {...getRootProps()}
        className={cn(
          'group cursor-pointer rounded-lg border border-dashed p-4 transition-colors sm:p-8',
          'flex flex-col items-center justify-center text-center',
          isDragActive ? 'border-primary' : 'border-muted-foreground/30',
          isDragAccept ? 'bg-primary/10' : '',
          isDragReject ? 'border-danger-700 bg-danger-100!' : '',
          disabled || isSizeLimitReached ? 'cursor-not-allowed opacity-50' : 'hover:border-primary/70'
        )}
      >
        <input {...getInputProps()} />
        <UploadCloud
          className={cn(
            'mb-3 size-8 stroke-1! transition-colors sm:mb-4 sm:size-12',
            isDragActive ? 'text-primary' : 'text-neutral-800',
            'group-hover:text-primary-700'
          )}
        />
        <p className="text-xs text-neutral-900 transition-colors group-hover:text-primary-800 sm:text-sm">
          {isDragActive
            ? isDragAccept
              ? 'Drop files here'
              : 'Some files may be rejected'
            : "Drag 'n' drop files here, or click to select"}
        </p>
        {maxSize && (
          <p className="mt-2 text-xs text-neutral-600 sm:text-sm">
            {formatBytes(totalFileSize)} / {formatBytes(maxSize)} used. You will be able to add more later.
          </p>
        )}
        {(disabled || isSizeLimitReached) && (
          <p className="mt-2 text-xs text-destructive sm:text-sm">
            {isSizeLimitReached ? 'Size limit reached.' : 'File limit reached or uploader disabled.'}
          </p>
        )}
      </div>
      {uploadableFiles.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-medium text-muted-foreground sm:text-sm">Uploading Files:</h3>
          <div ref={scrollContainerRef} className="max-h-60 w-full space-y-2 overflow-y-auto sm:space-y-3">
            {uploadableFiles.map((uf) => (
              <div key={uf.id} className="flex items-center justify-between rounded-md border bg-muted/30 p-2 sm:p-3">
                <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                  {uf.status === 'error' ? (
                    <XCircle className="h-5 w-5 shrink-0 text-danger sm:h-6 sm:w-6" />
                  ) : (
                    <FileIcon size="2" variant={'surface'} type={uf.fileType} />
                  )}
                  <div className="flex min-w-0 flex-col">
                    <p className="truncate text-xs font-medium sm:text-sm" title={uf.fileName}>
                      {uf.fileName}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatBytes(uf.fileSize)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                  {uf.status === 'uploading' && <span className="text-xs text-neutral-900">Uploading...</span>}
                  {uf.status === 'deleting' && <span className="text-xs text-neutral-900">Deleting...</span>}
                  {uf.status === 'success' && <span className="text-xs text-green-600">Uploaded</span>}
                  {uf.status === 'error' && (
                    <span className="truncate text-xs text-red-600" title={uf.error}>
                      Error
                    </span>
                  )}
                  {(uf.status === 'pending' || uf.status === 'uploading' || uf.status === 'deleting') && (
                    <Spinner className="h-4 w-4" />
                  )}
                  {(uf.status === 'success' || uf.status === 'error') && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 sm:h-7 sm:w-7"
                      onClick={() => handleDeleteFile(uf.id, uf.status)}
                    >
                      <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="sr-only">Remove file</span>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {rejectedFiles.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-medium text-destructive sm:text-sm">Rejected Files:</h3>
          <div className="max-h-60 space-y-2 overflow-y-auto">
            {rejectedFiles.map(({ file, errors }, index) => (
              <Alert variant="destructive" className="p-2 sm:p-3" key={`${file.name}-${index}`}>
                <div className="flex items-center justify-between">
                  <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                    <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6" />
                    <div className="flex min-w-0 flex-col">
                      <AlertTitle className="mb-0 text-xs sm:text-sm">
                        {file.name} ({formatBytes(file.size)})
                      </AlertTitle>
                      <AlertDescription className="text-xs">
                        {errors.length === 1 ? (
                          <span>{formatErrorMessage(errors[0]!)}</span>
                        ) : (
                          <ul className="list-inside list-disc">
                            {errors.map((error) => (
                              <li key={error.code}>{formatErrorMessage(error)}</li>
                            ))}
                          </ul>
                        )}
                      </AlertDescription>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 sm:h-7 sm:w-7"
                    onClick={() => removeRejectedFile(file.name)}
                  >
                    <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="sr-only">Dismiss</span>
                  </Button>
                </div>
              </Alert>
            ))}
          </div>
        </div>
      )}
    </>
  )
}
